import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  SimpleChanges
} from '@angular/core';

import {TranslateService} from '@ngx-translate/core';

import {EMPTY, merge, Observable, Subject, timer} from 'rxjs';
import {catchError, exhaustMap, filter, finalize, switchMap, take, takeUntil, tap, timeout} from 'rxjs/operators';

import {
  ChartRefineResponse,
  ChartSuggestion,
  ReportAnalysis,
  ReportAnalysisResult,
  ReportAnalysisStatus,
  ReportAnalyticsRequest
} from './report-analytics.interfaces';
import {ReportAnalyticsService} from './report-analytics.service';

import {Destroyable} from '../../destroyable.component';
import {NotificationFromSocket, NotificationType} from '../../inbox/shared/communications.interface';
import {NotificationSocketService} from '../../shared/api/notification-socket.service';
import {buildChartOption} from '../../shared/charts/chart-spec/chart-spec-option.builder';
import {ChartLabels, ChartType} from '../../shared/charts/chart-spec/chart-spec.interfaces';
import {Scss} from '../../shared/utils/scss';

export type AnalyticsViewState = 'loading' | 'done' | 'empty' | 'error';

export const POLL_INTERVAL_MS = 5000;
export const GIVE_UP_AFTER_MS = 6 * 60 * 1000;
const NOT_FOUND = 404;
const CHART_PALETTE_VARIABLE = 'chart-color-palette';
const CHART_NEUTRAL_VARIABLE = 'chart-neutral';

interface RenderedChart {
  id: number;
  option: Record<string, unknown>;
}

@Component({
  selector: 'gp-report-analytics',
  templateUrl: './report-analytics.component.html',
  styleUrls: ['./report-analytics.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportAnalyticsComponent extends Destroyable implements OnChanges {

  @Input() public request: ReportAnalyticsRequest;
  @Input() public refreshToken = 0;

  public state: AnalyticsViewState = 'loading';
  public result: ReportAnalysisResult;
  public suggestions: ChartSuggestion[] = [];
  public selectedIndex = 0;
  public renderedCharts: RenderedChart[] = [];
  public refineInstruction = '';
  public refining = false;
  public refineMessage: string;
  public refineFollowUps: string[] = [];

  public readonly ChartType = ChartType;

  private currentJobId: string;
  private restartedAfterNotFound = false;
  private renderCount = 0;
  private readonly cancel$ = new Subject<void>();

  constructor(private analyticsService: ReportAnalyticsService,
              private notificationSocketService: NotificationSocketService,
              private translate: TranslateService,
              private cd: ChangeDetectorRef) {
    super();
  }

  public get selected(): ChartSuggestion | undefined {
    return this.suggestions[this.selectedIndex];
  }

  public get isApproximate(): boolean {
    return !!this.selected?.data.approximate;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!this.request) {
      return;
    }
    const refreshed = !!changes.refreshToken && !changes.refreshToken.firstChange;
    if (refreshed) {
      this.analyticsService.forget(this.request);
    }
    const cached = this.analyticsService.cached(this.request);
    if (cached) {
      this.cancel$.next();
      this.show(cached);
      return;
    }
    this.restartedAfterNotFound = false;
    this.startAnalysis();
  }

  public retry(): void {
    this.restartedAfterNotFound = false;
    this.startAnalysis();
  }

  public select(index: number): void {
    this.selectedIndex = index;
    this.analyticsService.rememberSelection(this.request, index);
    this.clearRefineFeedback();
    this.renderSelected();
  }

  public applyFollowUp(followUp: string): void {
    this.refineInstruction = followUp;
    this.submitRefine();
  }

  public submitRefine(): void {
    const instruction = this.refineInstruction?.trim();
    if (!instruction || this.refining || !this.selected) {
      return;
    }
    this.refining = true;
    this.clearRefineFeedback();
    const request = this.request;
    this.analyticsService.refine(this.currentJobId, this.selected.spec, instruction).pipe(
      tap(response => {
        if (response.answerable && response.suggestion) {
          this.suggestions = this.suggestions.map((suggestion, index) =>
            index === this.selectedIndex ? response.suggestion : suggestion);
          this.analyticsService.remember(request, {
            jobId: this.currentJobId,
            status: ReportAnalysisStatus.DONE,
            result: {...this.result, suggestions: this.suggestions}
          });
          this.refineInstruction = '';
          this.renderSelected();
        } else {
          this.refineMessage = this.notAnswerableMessage(response);
          this.refineFollowUps = response.followUps ?? [];
        }
      }),
      catchError(() => {
        this.refineMessage = this.translate.instant('REPORTS.ai_analytics_refine_error');
        return EMPTY;
      }),
      finalize(() => {
        this.refining = false;
        this.cd.markForCheck();
      }),
      takeUntil(this.cancel$),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  public trackChart(_index: number, chart: RenderedChart): number {
    return chart.id;
  }

  private startAnalysis(): void {
    this.cancel$.next();
    this.state = 'loading';
    this.clearRefineFeedback();
    this.cd.markForCheck();
    const request = this.request;
    this.analyticsService.start(request).pipe(
      tap((jobId: string) => this.currentJobId = jobId),
      switchMap((jobId: string) => this.waitForCompletion(jobId)),
      tap((analysis: ReportAnalysis) => this.onCompleted(request, analysis)),
      catchError(error => {
        this.onFailure(error);
        return EMPTY;
      }),
      takeUntil(this.cancel$),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  private waitForCompletion(jobId: string): Observable<ReportAnalysis> {
    const notified$ = this.notificationSocketService.messages$().pipe(
      filter((message: NotificationFromSocket<string>) =>
        message.notificationType === NotificationType.REPORT_ANALYTICS && message.datas?.jobId === jobId)
    );
    return merge(notified$, timer(POLL_INTERVAL_MS, POLL_INTERVAL_MS)).pipe(
      exhaustMap(() => this.analyticsService.get(jobId)),
      filter((analysis: ReportAnalysis) =>
        analysis.status === ReportAnalysisStatus.DONE || analysis.status === ReportAnalysisStatus.FAILED),
      take(1),
      timeout(GIVE_UP_AFTER_MS)
    );
  }

  private onCompleted(request: ReportAnalyticsRequest, analysis: ReportAnalysis): void {
    if (analysis.jobId !== this.currentJobId) {
      return;
    }
    if (analysis.status === ReportAnalysisStatus.FAILED) {
      this.state = 'error';
      this.cd.markForCheck();
      return;
    }
    this.analyticsService.remember(request, analysis);
    this.show(analysis);
  }

  private onFailure(error: { status?: number }): void {
    if (error?.status === NOT_FOUND && !this.restartedAfterNotFound) {
      this.restartedAfterNotFound = true;
      this.startAnalysis();
      return;
    }
    this.state = 'error';
    this.cd.markForCheck();
  }

  private show(analysis: ReportAnalysis): void {
    this.currentJobId = analysis.jobId;
    this.result = analysis.result;
    this.suggestions = [...(analysis.result?.suggestions ?? [])];
    const rememberedIndex = this.analyticsService.cachedSelection(this.request);
    this.selectedIndex = rememberedIndex < this.suggestions.length ? rememberedIndex : 0;
    this.state = this.suggestions.length ? 'done' : 'empty';
    this.renderSelected();
  }

  private notAnswerableMessage(response: ChartRefineResponse): string {
    if (response.missingFields?.length) {
      return this.translate.instant('REPORTS.ai_analytics_missing_fields', {fields: response.missingFields.join(', ')});
    }
    return response.message || this.translate.instant('REPORTS.ai_analytics_refine_not_supported');
  }

  private clearRefineFeedback(): void {
    this.refineMessage = null;
    this.refineFollowUps = [];
  }

  private renderSelected(): void {
    const selected = this.selected;
    const option = selected
      ? buildChartOption(selected.spec, selected.data, this.chartLabels(), this.palette(), Scss.variable(CHART_NEUTRAL_VARIABLE))
      : null;
    this.renderedCharts = option ? [{id: ++this.renderCount, option}] : [];
    this.cd.markForCheck();
  }

  private chartLabels(): ChartLabels {
    return {
      other: this.translate.instant('REPORTS.ai_analytics_other'),
      empty: this.translate.instant('REPORTS.ai_analytics_empty_value'),
      count: this.translate.instant('REPORTS.ai_analytics_rows')
    };
  }

  private palette(): string[] {
    return (Scss.variable(CHART_PALETTE_VARIABLE) || '')
      .split(',')
      .map((color: string) => color.trim())
      .filter((color: string) => !!color);
  }
}
